import { Link, useLocation } from "@tanstack/react-router"
import { useOrganization, useUser, useAuth } from "@clerk/clerk-react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { currentOrgQuery, dashboardStatsQuery, projectListQuery } from "@/lib/queries"
import { cn } from "@/lib/utils"
import { IconHome, IconFolder, IconSettings, IconChevronRight } from "@tabler/icons-react"
import { Avatar, AvatarImage, AvatarFallback } from "@/ui/avatar"
import { Badge } from "@/ui/badge"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/ui/dropdown-menu"
import { useClerk } from "@clerk/clerk-react"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarGroup,
  SidebarGroupContent,
} from "@/ui/sidebar"
import { Separator } from "@/ui/separator"

export function AppSidebar() {
  const { data: orgData } = useQuery(currentOrgQuery())
  const { organization } = useOrganization()
  const { user } = useUser()
  const { has } = useAuth()
  const { signOut, openUserProfile } = useClerk()
  const location = useLocation()
  const queryClient = useQueryClient()
  const hasGitHub = orgData?.organization?.hasGitHubConnected

  // Determine current plan
  const getPlanInfo = () => {
    if (has?.({ plan: "orgenterprise" })) return { name: "Enterprise", variant: "default" as const }
    if (has?.({ plan: "orgpro" })) return { name: "Pro", variant: "default" as const }
    if (has?.({ plan: "orgbasic" })) return { name: "Basic", variant: "secondary" as const }
    return { name: "Free", variant: "outline" as const }
  }
  const planInfo = getPlanInfo()

  const handlePrefetch = (to: string) => {
    const staleTime = 60000
    if (to === "/dashboard") {
      queryClient.prefetchQuery({ ...dashboardStatsQuery(), staleTime })
    } else if (to === "/dashboard/projects") {
      queryClient.prefetchQuery({ ...projectListQuery(), staleTime })
    } else if (to === "/dashboard/settings") {
      queryClient.prefetchQuery({ ...currentOrgQuery(), staleTime })
    }
  }

  const navItems = [
    { to: "/dashboard", label: "Overview", icon: IconHome, exact: true },
    { to: "/dashboard/projects", label: "Projects", icon: IconFolder },
    { to: "/dashboard/settings", label: "Settings", icon: IconSettings },
  ]

  return (
    <Sidebar>
      <SidebarHeader className="p-3">
        <Link
          to="/dashboard"
          className="flex items-center gap-2.5 px-3 py-2.5 hover:opacity-80 transition-opacity"
        >
          <img src="/loomii-pink-black.png" alt="Loomii" className="w-7 h-7" />
          <span className="text-base font-semibold text-foreground">Loomii</span>
        </Link>
      </SidebarHeader>

      <div className="px-3">
        <Separator />
      </div>

      <SidebarContent className="pt-2">
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu className="gap-1 px-2">
              {navItems.map((item) => {
                const isActive = item.exact
                  ? location.pathname === item.to
                  : location.pathname.startsWith(item.to)

                return (
                  <SidebarMenuItem key={item.to}>
                    <SidebarMenuButton
                      render={<Link to={item.to} />}
                      isActive={isActive}
                      onMouseEnter={() => handlePrefetch(item.to)}
                      className={cn(
                        "h-10 px-3 rounded-lg transition-all",
                        isActive
                          ? "bg-primary/10 text-primary font-medium"
                          : "text-muted-foreground hover:text-foreground hover:bg-sidebar-accent"
                      )}
                    >
                      <item.icon className={cn("w-4 h-4", isActive && "text-primary")} />
                      <span>{item.label}</span>
                      {item.label === "Settings" && !hasGitHub && (
                        <span className="ml-auto w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                      )}
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="p-3">
        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <button className="w-full px-2 py-2 rounded-lg hover:bg-sidebar-accent transition-colors flex items-center gap-2.5 text-left">
                <Avatar size="sm" className="w-8 h-8 rounded-lg">
                  <AvatarImage src={organization?.imageUrl} />
                  <AvatarFallback className="rounded-lg text-xs bg-primary/10 text-primary">
                    {organization?.name?.[0]?.toUpperCase() || "O"}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-medium truncate">{organization?.name || "Organization"}</span>
                    <Badge variant={planInfo.variant} className="text-[8px] px-1 py-0 h-4">
                      {planInfo.name}
                    </Badge>
                  </div>
                  <p className="text-[10px] text-muted-foreground truncate">
                    {user?.primaryEmailAddress?.emailAddress}
                  </p>
                </div>
                <IconChevronRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
              </button>
            }
          />
          <DropdownMenuContent side="right" align="end" className="w-48">
            <div className="px-2 py-1.5">
              <p className="text-xs font-medium">{user?.fullName}</p>
              <p className="text-[10px] text-muted-foreground truncate">
                {user?.primaryEmailAddress?.emailAddress}
              </p>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => openUserProfile()}>
              Manage account
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => signOut()}
              className="text-destructive focus:text-destructive"
            >
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarFooter>
    </Sidebar>
  )
}
